export const SCORE_DOMAIN_MIN = 0;
export const SCORE_DOMAIN_MAX = 1;

export function normalizeHistogramScore(score) {
  const value = Number(score);
  return Number.isFinite(value) ? value : null;
}

function clampScoreDomain(value) {
  return Math.max(SCORE_DOMAIN_MIN, Math.min(SCORE_DOMAIN_MAX, value));
}

/**
 * Bin layout: half-width edge bins spanning slightly below 0 and above 1,
 * with (binCount - 2) full-width interior bins covering the rest of [0, 1].
 */
export function histogramBinLayout(binCount) {
  const count = Math.max(3, binCount);
  const fullWidth = 1 / (count - 1);
  const halfWidth = fullWidth / 2;

  return Array.from({ length: count }, (_, index) => {
    if (index === 0) {
      return { x0: -halfWidth, x1: halfWidth };
    }
    if (index === count - 1) {
      return { x0: 1 - halfWidth, x1: 1 + halfWidth };
    }

    return {
      x0: halfWidth + (index - 1) * fullWidth,
      x1: halfWidth + index * fullWidth,
    };
  });
}

export function findHistogramBinIndex(value, bins) {
  for (let index = bins.length - 1; index >= 0; index -= 1) {
    const { x0, x1 } = bins[index];
    const isLast = index === bins.length - 1;

    if (value >= x0 && (isLast ? value <= x1 : value < x1)) {
      return index;
    }
  }

  return 0;
}

/**
 * Assign weighted contributions to histogram bins (0 and 1 live in edge bins).
 */
export function weightedHistogramBins(contributions, binCount) {
  const layout = histogramBinLayout(binCount);

  const bins = layout.map(({ x0, x1 }) => ({
    x0,
    x1,
    xCenter: clampScoreDomain((x0 + x1) / 2),
    yValue: 0,
  }));

  for (const { score, fraction } of contributions) {
    const value = normalizeHistogramScore(score);
    if (value == null) {
      continue;
    }

    const index = findHistogramBinIndex(value, layout);
    bins[index].yValue += fraction ?? 0;
  }

  return bins;
}
